/**
 * Client-side image compression helper.
 *
 * Goal: keep occurrence-book photo uploads ≤ 2 MB so the operator can shoot evidence
 * on a modern phone (≈ 6-12 MB per JPEG) without us round-tripping huge payloads.
 *
 * Behaviour:
 *  - Only compresses MIME types starting with `image/` (PDFs/Word/text are passed
 *    through untouched).
 *  - HEIC is forwarded as-is (the library doesn't decode it in the browser).
 *  - Already-small images (< 2 MB) are returned unchanged — never re-encode a
 *    tiny screenshot just to claim "we compressed it".
 *  - The original filename + extension is preserved on the returned File.
 */
import imageCompression from 'browser-image-compression';

const MAX_MB = 2;
const MAX_BYTES = MAX_MB * 1024 * 1024;

const isCompressibleImage = (file) => {
  if (!file?.type?.startsWith('image/')) return false;
  // HEIC needs a separate decoder; let the backend / browser handle it raw.
  if (file.type === 'image/heic' || file.type === 'image/heif') return false;
  return true;
};

export const compressImageIfNeeded = async (file) => {
  if (!file) return file;
  if (!isCompressibleImage(file)) return file;
  if (file.size <= MAX_BYTES) return file;

  const compressed = await imageCompression(file, {
    maxSizeMB: MAX_MB,
    maxWidthOrHeight: 2560,   // ample for evidence; ~5MP equivalent
    useWebWorker: true,
    initialQuality: 0.82,
    fileType: file.type === 'image/png' ? 'image/jpeg' : file.type,
  });

  // browser-image-compression returns a Blob — re-wrap as File so the upload
  // keeps the original filename. If the conversion changed the encoding from
  // PNG → JPEG, swap the extension so the server sees a consistent name.
  let outName = file.name;
  if (file.type === 'image/png' && compressed.type === 'image/jpeg') {
    outName = file.name.replace(/\.png$/i, '.jpg');
  }
  return new File([compressed], outName, {
    type: compressed.type,
    lastModified: Date.now(),
  });
};

export const IMAGE_COMPRESS_MAX_MB = MAX_MB;
